import React from "react";
import { Icon } from "../icon/Icon.jsx";

/* Ringover Button — solid, soft-grey, ghost, and danger variants.
   Pill-free, 8px radius, Inter medium. */

const SIZES = {
  sm: { height: 32, padding: "0 12px", font: "var(--text-sm-size)", gap: 6, icon: 16 },
  md: { height: 40, padding: "0 16px", font: "var(--text-sm-size)", gap: 8, icon: 18 },
  lg: { height: 48, padding: "0 20px", font: "var(--text-md-size)", gap: 8, icon: 20 },
};

const VARIANTS = {
  primary: { background: "var(--color-brand)", color: "var(--text-on-brand)", border: "transparent" },
  accent: { background: "var(--rg-empower-600)", color: "var(--text-on-brand)", border: "transparent" },
  secondary: { background: "var(--rg-grey-300)", color: "var(--text-primary)", border: "transparent" },
  ghost: { background: "transparent", color: "var(--text-body)", border: "transparent" },
  outline: { background: "var(--surface-card)", color: "var(--text-primary)", border: "var(--border-default)" },
  danger: { background: "var(--color-danger)", color: "var(--text-on-brand)", border: "transparent" },
};

export function Button({
  children,
  variant = "primary",
  size = "md",
  leftIcon,
  rightIcon,
  fullWidth = false,
  disabled = false,
  style,
  ...rest
}) {
  const s = SIZES[size] || SIZES.md;
  const v = VARIANTS[variant] || VARIANTS.primary;
  const [hover, setHover] = React.useState(false);
  const [active, setActive] = React.useState(false);

  const hoverOverlay = hover && !disabled
    ? (variant === "ghost" || variant === "outline" ? "var(--rg-grey-300)" : "rgba(0,0,0,0.06)")
    : null;

  return (
    <button
      type="button"
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: s.gap,
        height: s.height,
        padding: s.padding,
        width: fullWidth ? "100%" : "auto",
        fontFamily: "var(--font-sans)",
        fontWeight: "var(--weight-medium)",
        fontSize: s.font,
        lineHeight: 1,
        color: v.color,
        background: v.background,
        boxShadow: v.border !== "transparent" ? `inset 0 0 0 1px ${v.border}` : "none",
        border: "none",
        borderRadius: "var(--radius-sm)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        transform: active && !disabled ? "scale(0.97)" : "scale(1)",
        transition: "transform var(--duration-fast) var(--ease-standard), background var(--duration-base) var(--ease-standard), filter var(--duration-base) var(--ease-standard)",
        filter: hover && !disabled && variant !== "ghost" && variant !== "outline" ? "brightness(0.94)" : "none",
        backgroundImage: hoverOverlay ? `linear-gradient(${hoverOverlay},${hoverOverlay})` : "none",
        position: "relative",
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {leftIcon && <Icon name={leftIcon} size={s.icon} />}
      {children}
      {rightIcon && <Icon name={rightIcon} size={s.icon} />}
    </button>
  );
}

export default Button;
